import React from 'react';
import { GAME_CONFIG } from '../config/gameConfig';
import { preloadDictionary, validateWords } from '../services/dictionaryService';

const wordValidationKey = (word) => String(word || '').toUpperCase();

export const useScrabbleWordValidation = ({ evaluation }) => {
  const [dictionaryStatus, setDictionaryStatus] = React.useState('idle');
  const [dictionaryError, setDictionaryError] = React.useState('');
  const [wordValidation, setWordValidation] = React.useState({});

  const wordsNeedingValidation = React.useMemo(
    () => evaluation.wordsForDictionary.map((word) => wordValidationKey(word.text)),
    [evaluation.wordsForDictionary]
  );

  const wordsValidationSignature = React.useMemo(
    () => wordsNeedingValidation.join('|'),
    [wordsNeedingValidation]
  );

  React.useEffect(() => {
    preloadDictionary();
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const wordsToValidate = wordsValidationSignature
      ? wordsValidationSignature.split('|').filter(Boolean)
      : [];

    if (!evaluation.hasTiles) {
      return () => {
        cancelled = true;
      };
    }

    if (!wordsToValidate.length) {
      return () => {
        cancelled = true;
      };
    }

    setDictionaryStatus('loading');
    setDictionaryError('');

    const timeout = setTimeout(async () => {
      try {
        const result = await validateWords(wordsToValidate);
        if (cancelled) {
          return;
        }

        setWordValidation(result);
        setDictionaryStatus('ready');
      } catch (error) {
        if (cancelled) {
          return;
        }

        setDictionaryStatus('error');
        setDictionaryError('Dictionary is unavailable. Submit is blocked until it reconnects.');
      }
    }, 180);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [evaluation.hasTiles, wordsValidationSignature]);

  const wordsWithValidation = React.useMemo(
    () =>
      evaluation.words.map((word) => {
        if (word.cells.length < GAME_CONFIG.minWordLength) {
          return {
            ...word,
            isValid: false,
            validity: 'invalid',
          };
        }

        if (dictionaryStatus === 'loading') {
          return {
            ...word,
            isValid: false,
            validity: 'pending',
          };
        }

        const key = wordValidationKey(word.text);
        const isValid = Boolean(wordValidation[key]);

        return {
          ...word,
          isValid,
          validity: isValid ? 'valid' : 'invalid',
        };
      }),
    [evaluation.words, wordValidation, dictionaryStatus]
  );

  const tileValidationState = React.useMemo(() => {
    const highlight = {};

    wordsWithValidation.forEach((word) => {
      const stateValue =
        word.validity === 'valid'
          ? 'valid'
          : word.validity === 'pending'
            ? 'pending'
            : 'invalid';

      word.cells.forEach((tileId) => {
        const existing = highlight[tileId];
        if (existing === 'invalid') {
          return;
        }

        if (existing === 'pending' && stateValue === 'valid') {
          return;
        }

        highlight[tileId] = stateValue;
      });
    });

    return highlight;
  }, [wordsWithValidation]);

  const isDictionaryValid = React.useMemo(
    () =>
      wordsWithValidation
        .filter((word) => word.cells.length >= GAME_CONFIG.minWordLength)
        .every((word) => word.isValid),
    [wordsWithValidation]
  );

  const resetWordValidationState = React.useCallback(() => {
    setWordValidation({});
    setDictionaryStatus('idle');
    setDictionaryError('');
  }, []);

  return {
    dictionaryStatus,
    dictionaryError,
    wordsWithValidation,
    tileValidationState,
    isDictionaryValid,
    resetWordValidationState,
  };
};
